import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  updatePassword,
  linkWithCredential,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signOut,
  updateProfile,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import { auth, db, functions } from "./firebase";
import { welcomeCopy } from "./notificationCopy";
import { REGISTRATION_OPEN, REGISTRATION_CLOSED_CODE } from "../config/registration";

/**
 * Error con el mismo shape que los de Firebase (`.code`) para que la UI lo trate igual
 * que cualquier otro error de auth — ver AUTH_ERROR_KEYS en auth-errors.js.
 */
function registrationClosedError() {
  const err = new Error("Registrations are closed while the platform is being built.");
  err.code = REGISTRATION_CLOSED_CODE;
  return err;
}

/**
 * Create the Firestore profile for a user if it does not exist yet.
 * Used by both email registration and Google sign-in — a Google user signing in
 * for the first time has no profile doc, and every gated page reads it.
 * Never overwrites an existing profile (role/enrolledPrograms must survive).
 */
async function ensureUserProfile(user, { displayName, language }) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;

  await setDoc(ref, {
    displayName: displayName || user.displayName || "",
    email: user.email,
    role: "student",
    language,
    enrolledPrograms: [],
    createdAt: serverTimestamp(),
  });

  // Fase 5 de notificaciones-sistema.md: correo de bienvenida vía el bus. Solo corre en
  // el registro real (guardado por el !snap.exists() de arriba), cubre tanto email/password
  // como el primer login con Google. No debe tumbar el registro si falla (mismo criterio
  // que sendEmailVerification más abajo). Copy final en notificationCopy.js (Fase 2 de
  // diseno-sistema-email.md) — explica qué es el ministerio y qué gana la persona con la
  // cuenta, no solo agradece el registro.
  try {
    const lang = language === "en" ? "en" : "es";
    const { title, body, listItems } = welcomeCopy(lang);
    await addDoc(collection(db, "notifications"), {
      userId: user.uid,
      type: "welcome",
      language: lang,
      payload: { title, body, listItems },
      status: "pending",
      readAt: null,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("[auth] no se pudo crear la notificación de bienvenida:", err?.code);
  }
}

/**
 * Register a new user with email/password and create their Firestore profile.
 * Sends a verification email, but nothing is gated on it — an unverified account
 * works exactly as before. Verification exists so a typo in the address surfaces
 * before the person needs to recover the password.
 */
export async function registerUser({ email, password, displayName, language = "es" }) {
  // Puerta de altas (src/config/registration.js). Corta antes de tocar Firebase, para que
  // ni siquiera llegue a existir un usuario de Auth a medio crear.
  if (!REGISTRATION_OPEN) throw registrationClosedError();

  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const user = credential.user;

  await updateProfile(user, { displayName });
  await ensureUserProfile(user, { displayName, language });

  // Reemplaza el sendEmailVerification() default de Firebase (dominio compartido, siempre
  // cae en spam) por el envío vía Resend desde nuestro propio dominio ya verificado — ver
  // functions/src/sendVerificationEmail.js. No debe tumbar el registro si falla (cuota, red).
  try {
    const sendVerificationEmail = httpsCallable(functions, "sendVerificationEmail");
    await sendVerificationEmail({ language });
  } catch (err) {
    console.warn("[auth] no se pudo enviar el correo de verificación:", err?.code);
  }

  return user;
}

/**
 * Sign in an existing user.
 */
export async function loginUser({ email, password }) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

/**
 * True when "continue with Google" can actually work here.
 *
 * `signInWithPopup` needs a real browser popup, which the Capacitor WebView does
 * not provide. Enabling it on native would show a button that always fails, so
 * the UI hides it instead. To support native later, add
 * `@capacitor-firebase/authentication` (native Google flow) and widen this check
 * — no other call site needs to change.
 */
export function isGoogleSignInAvailable() {
  if (typeof window === "undefined") return false;
  return !Capacitor.isNativePlatform();
}

/**
 * Sign in (or register) with Google, creating the Firestore profile on first use.
 *
 * Requires Google to be enabled in Firebase Console → Authentication → Sign-in
 * method, with both production domains listed under Authorized domains.
 */
export async function signInWithGoogle({ language = "es" } = {}) {
  const provider = new GoogleAuthProvider();
  const credential = await signInWithPopup(auth, provider);

  // Con los registros cerrados, "Continuar con Google" solo sirve para entrar a una cuenta
  // que ya existe. Firebase crea el usuario dentro del mismo popup, así que la única forma
  // de no dejar una cuenta huérfana es borrarla en cuanto sabemos que es nueva — antes de
  // escribir el perfil en Firestore, la notificación de bienvenida o cualquier otro rastro.
  if (!REGISTRATION_OPEN && getAdditionalUserInfo(credential)?.isNewUser) {
    try {
      await credential.user.delete();
    } catch (err) {
      console.warn("[auth] no se pudo borrar la cuenta de Google recién creada:", err?.code);
      await signOut(auth);
    }
    throw registrationClosedError();
  }

  await ensureUserProfile(credential.user, {
    displayName: credential.user.displayName,
    language,
  });
  return credential.user;
}

/**
 * Pide el enlace para recuperar la contraseña. Lo envía la función requestPasswordReset
 * con el correo de la marca y en el idioma del sitio (cuenta-contrasena-interna.md), no la
 * plantilla de Firebase. La función responde igual exista o no la cuenta: la UI tampoco
 * debe distinguir.
 */
export async function requestPasswordReset(email, language = "es") {
  const call = httpsCallable(functions, "requestPasswordReset");
  await call({ email: email.trim(), language: language === "en" ? "en" : "es" });
}

/** Comprueba el código del enlace del correo y devuelve a qué correo corresponde. */
export async function checkPasswordResetCode(oobCode) {
  return verifyPasswordResetCode(auth, oobCode);
}

/** Guarda la clave nueva con el código del enlace del correo. */
export async function savePasswordWithResetCode(oobCode, newPassword) {
  await confirmPasswordReset(auth, oobCode, newPassword);
}

/** Qué forma de entrar tiene la cuenta abierta: 'password' (con o sin Google), 'google' o null. */
export function accountSignInKind(user) {
  const providers = (user?.providerData || []).map((p) => p.providerId);
  if (providers.includes("password")) return "password";
  if (providers.includes("google.com")) return "google";
  return null;
}

/** Cuenta con clave: confirma la clave actual y la cambia. */
export async function changePassword(currentPassword, newPassword) {
  const user = auth.currentUser;
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
  await updatePassword(user, newPassword);
}

/**
 * Cuenta que solo entra con Google: confirma con Google y le agrega una clave. Solo suma
 * una forma de entrar a una cuenta que ya existe; no crea cuentas (registros cerrados).
 */
export async function addPassword(newPassword) {
  const user = auth.currentUser;
  await reauthenticateWithPopup(user, new GoogleAuthProvider());
  await linkWithCredential(user, EmailAuthProvider.credential(user.email, newPassword));
}

/**
 * Sign out the current user.
 */
export async function logoutUser() {
  await signOut(auth);
}

/**
 * Record that a student is asking for a mentor (discipulado general — users/{uid}.mentorId
 * is otherwise admin-only, ver S2 "La puerta del acompañamiento"). Admin fulfills it from
 * /admin/ with the existing mentor selector; assignMentor() clears this once assigned.
 */
export async function requestMentor(uid) {
  await updateDoc(doc(db, "users", uid), { mentorRequestedAt: serverTimestamp() });
}

/**
 * Record that a user is offering to become an acompañante (S15a "La puerta del
 * liderazgo") — la inversa de requestMentor(). Nunca cambia el rol; el admin decide
 * desde /admin/ después de verlo, mismo patrón que mentorRequestedAt.
 */
export async function requestLeadershipRole(uid, note) {
  await updateDoc(doc(db, "users", uid), {
    leadershipInterestAt: serverTimestamp(),
    leadershipInterestNote: note || "",
  });
}

/**
 * Persist that the student has seen the "ya tienes acompañante" banner for this
 * specific mentorId. Stored on the profile doc (not localStorage) so the ack
 * survives across browsers/devices — a localStorage-only ack reappears every time
 * the person logs in from a new browser, which is the bug this replaces.
 */
export async function ackMentorAssigned(uid, mentorId) {
  await updateDoc(doc(db, "users", uid), { mentorAckedId: mentorId });
}

/**
 * Persist that the person has seen the "te ascendieron a mentor/admin" banner for
 * this specific role. Same rationale as ackMentorAssigned() above.
 */
export async function ackRolePromotion(uid, role) {
  await updateDoc(doc(db, "users", uid), { roleAckedAs: role });
}

/**
 * Fetch the Firestore user profile document.
 */
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { uid, ...snap.data() } : null;
}

/**
 * Subscribe to Firebase Auth state changes.
 * Returns an unsubscribe function.
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}
